import React, { createContext, useContext, useState, useCallback } from 'react';

// ナビゲーション履歴を管理するコンテキスト
const NavigationContext = createContext();

export const useNavigationHistory = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      'useNavigationHistory must be used within NavigationProvider'
    );
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  // 遷移履歴を管理（最大3階層）
  const [navigationHistory, setNavigationHistory] = useState([]);

  // 遷移履歴を追加
  const pushHistory = useCallback((path, title) => {
    setNavigationHistory((prev) => {
      // Introduction、Login等の除外パス
      const excludedPaths = ['/introduction', '/login', '/register', '/kyc'];
      if (excludedPaths.includes(path)) {
        return prev;
      }

      // 不正なタイトル（「真決DAO」等）を含む履歴を削除
      const cleanedHistory = prev.filter(
        (item) =>
          item.title !== '真決DAO' &&
          item.title !== null &&
          item.title !== undefined &&
          item.title.trim() !== ''
      );

      // 申請系画面での不正な遷移を防ぐ
      // 申請状況照会からストア申請・DAO申請への遷移は履歴をリセット
      const lastItem = cleanedHistory[cleanedHistory.length - 1];
      if (
        lastItem &&
        lastItem.title === '申請状況照会' &&
        (title === 'ストア申請' || title === 'DAO化申請')
      ) {
        // 申請状況照会から申請画面への遷移時は、ダッシュボードからの遷移として処理
        return [{ path, title }];
      }

      // 同じパスが既に履歴にある場合、そこまでの履歴に戻す（重複を防ぐ）
      const existingIndex = cleanedHistory.findIndex(
        (item) => item.path === path
      );
      if (existingIndex !== -1) {
        return cleanedHistory.slice(0, existingIndex + 1);
      }

      // 循環参照やおかしな遷移を防ぐ - 同じタイトルが既にある場合は除去
      const historyWithoutSameTitle = cleanedHistory.filter(
        (item) => item.title !== title
      );

      // 新しい履歴を追加（最大3階層まで保持）
      const newHistory = [...historyWithoutSameTitle, { path, title }];

      return newHistory.slice(-3);
    });
  }, []);

  // 遷移履歴をクリア
  const clearHistory = useCallback(() => {
    setNavigationHistory([]);
  }, []);

  // 特定のパスまでの履歴を設定（パンくず用）
  const setHistoryTo = useCallback((targetPath) => {
    setNavigationHistory((prev) => {
      const index = prev.findIndex((item) => item.path === targetPath);
      if (index !== -1) {
        return prev.slice(0, index + 1);
      }
      return prev;
    });
  }, []);

  // 現在のパンくず情報を取得
  const getBreadcrumbs = useCallback(() => {
    return navigationHistory;
  }, [navigationHistory]);

  const value = {
    navigationHistory,
    pushHistory,
    clearHistory,
    setHistoryTo,
    getBreadcrumbs,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
